import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Bell, BellOff, Plus, Trash2 } from "lucide-react";
import {
  ALERT_METRICS,
  type AlertMetric,
  type AlertOperator,
  type AlertRule,
  type AlertRuleCreate,
  type AlertSeverity,
  createAlertRule,
  deleteAlertRule,
  listAlertRules,
  updateAlertRule,
} from "@/api/endpoints/alerts";

const SEVERITY_STYLES: Record<AlertSeverity, string> = {
  info: "bg-sky-600/20 text-sky-300",
  warning: "bg-amber-600/20 text-amber-300",
  critical: "bg-red-600/20 text-red-300",
};

export function AlertRules() {
  const qc = useQueryClient();
  const { data: rules, isLoading } = useQuery({
    queryKey: ["alert-rules"],
    queryFn: listAlertRules,
    refetchInterval: 30_000,
  });
  const [open, setOpen] = useState(false);

  const createMut = useMutation({
    mutationFn: createAlertRule,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["alert-rules"] });
      setOpen(false);
    },
  });
  const toggleMut = useMutation({
    mutationFn: ({ id, enabled }: { id: number; enabled: boolean }) =>
      updateAlertRule(id, { enabled }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["alert-rules"] }),
  });
  const deleteMut = useMutation({
    mutationFn: deleteAlertRule,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["alert-rules"] }),
  });

  return (
    <div className="rounded-lg border border-gray-800 bg-gray-900">
      <div className="flex items-center justify-between border-b border-gray-800 px-4 py-3">
        <div>
          <h3 className="font-semibold text-gray-100">Alert rules</h3>
          <p className="text-xs text-gray-500">
            DB-backed thresholds evaluated every 60s. Breaches open an alert
            and, when <code>notify_ntfy</code> is set, push to ntfy.
          </p>
        </div>
        <button
          onClick={() => setOpen(true)}
          className="flex items-center gap-1 rounded bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-500"
        >
          <Plus className="h-4 w-4" /> New rule
        </button>
      </div>

      {isLoading ? (
        <div className="p-4 text-sm text-gray-500">Loading rules…</div>
      ) : !rules || rules.length === 0 ? (
        <div className="p-4 text-sm text-gray-500">
          No rules defined. Create one to start alerting on metric breaches.
        </div>
      ) : (
        <table className="w-full text-sm">
          <thead className="border-b border-gray-800 text-left text-xs uppercase text-gray-500">
            <tr>
              <th className="px-4 py-2">Name</th>
              <th className="px-4 py-2">Metric</th>
              <th className="px-4 py-2">Condition</th>
              <th className="px-4 py-2">Severity</th>
              <th className="px-4 py-2">Scope</th>
              <th className="px-4 py-2">ntfy</th>
              <th className="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-800">
            {rules.map((r) => (
              <AlertRuleRow
                key={r.id}
                rule={r}
                onToggle={() =>
                  toggleMut.mutate({ id: r.id, enabled: !r.enabled })
                }
                onDelete={() => {
                  if (confirm(`Delete rule "${r.name}"?`)) deleteMut.mutate(r.id);
                }}
              />
            ))}
          </tbody>
        </table>
      )}

      {open && (
        <CreateAlertRuleModal
          onClose={() => setOpen(false)}
          onSubmit={(payload) => createMut.mutate(payload)}
          pending={createMut.isPending}
          error={createMut.error ? String(createMut.error) : null}
        />
      )}
    </div>
  );
}

function AlertRuleRow({
  rule,
  onToggle,
  onDelete,
}: {
  rule: AlertRule;
  onToggle: () => void;
  onDelete: () => void;
}) {
  return (
    <tr className={rule.enabled ? "" : "opacity-50"}>
      <td className="px-4 py-2 font-medium text-gray-200">{rule.name}</td>
      <td className="px-4 py-2 font-mono text-xs text-gray-400">{rule.metric}</td>
      <td className="px-4 py-2 font-mono text-xs text-gray-300">
        {rule.operator} {rule.threshold}
      </td>
      <td className="px-4 py-2">
        <span
          className={`rounded px-1.5 py-0.5 text-xs ${SEVERITY_STYLES[rule.severity]}`}
        >
          {rule.severity}
        </span>
      </td>
      <td className="px-4 py-2 text-xs text-gray-400">
        {rule.scope || <span className="text-gray-600">—</span>}
      </td>
      <td className="px-4 py-2">
        {rule.notify_ntfy ? (
          <Bell className="h-4 w-4 text-emerald-400" />
        ) : (
          <BellOff className="h-4 w-4 text-gray-600" />
        )}
      </td>
      <td className="px-4 py-2">
        <div className="flex justify-end gap-2">
          <button
            onClick={onToggle}
            className="rounded border border-gray-700 bg-gray-800 px-2 py-0.5 text-xs text-gray-300 hover:bg-gray-700"
          >
            {rule.enabled ? "Disable" : "Enable"}
          </button>
          <button
            onClick={onDelete}
            aria-label="Delete rule"
            className="rounded border border-red-700/50 bg-red-700/10 px-2 py-0.5 text-xs text-red-300 hover:bg-red-700/20"
          >
            <Trash2 className="h-3 w-3" />
          </button>
        </div>
      </td>
    </tr>
  );
}

function CreateAlertRuleModal({
  onClose,
  onSubmit,
  pending,
  error,
}: {
  onClose: () => void;
  onSubmit: (p: AlertRuleCreate) => void;
  pending: boolean;
  error: string | null;
}) {
  const [name, setName] = useState("");
  const [metric, setMetric] = useState<AlertMetric>("node.ram_pct_free");
  const [operator, setOperator] = useState<AlertOperator>("<");
  const [threshold, setThreshold] = useState(10);
  const [severity, setSeverity] = useState<AlertSeverity>("warning");
  const [scope, setScope] = useState("");
  const [notifyNtfy, setNotifyNtfy] = useState(true);

  const canSubmit = name.trim().length > 0;
  const isPctMetric = metric.includes("pct");

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-lg border border-gray-800 bg-gray-900 p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="mb-4 text-lg font-semibold text-gray-100">New alert rule</h3>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!canSubmit) return;
            onSubmit({
              name,
              metric,
              operator,
              threshold,
              severity,
              scope: scope.trim() || null,
              notify_ntfy: notifyNtfy,
            });
          }}
          className="space-y-3"
        >
          <label className="block text-sm">
            <span className="text-gray-400">Name</span>
            <input
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-1 w-full rounded border border-gray-700 bg-gray-800 px-2 py-1.5 text-sm text-gray-100"
              placeholder="RAM low on site A"
            />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block text-sm">
              <span className="text-gray-400">Metric</span>
              <select
                value={metric}
                onChange={(e) => setMetric(e.target.value as AlertMetric)}
                className="mt-1 w-full rounded border border-gray-700 bg-gray-800 px-2 py-1.5 text-sm text-gray-100"
              >
                {ALERT_METRICS.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
            </label>
            <label className="block text-sm">
              <span className="text-gray-400">Severity</span>
              <select
                value={severity}
                onChange={(e) => setSeverity(e.target.value as AlertSeverity)}
                className="mt-1 w-full rounded border border-gray-700 bg-gray-800 px-2 py-1.5 text-sm text-gray-100"
              >
                <option value="info">info</option>
                <option value="warning">warning</option>
                <option value="critical">critical</option>
              </select>
            </label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="block text-sm">
              <span className="text-gray-400">Operator</span>
              <select
                value={operator}
                onChange={(e) => setOperator(e.target.value as AlertOperator)}
                className="mt-1 w-full rounded border border-gray-700 bg-gray-800 px-2 py-1.5 text-sm text-gray-100"
              >
                <option value="<">&lt;</option>
                <option value="<=">&le;</option>
                <option value=">">&gt;</option>
                <option value=">=">&ge;</option>
                <option value="==">=</option>
              </select>
            </label>
            <label className="block text-sm">
              <span className="text-gray-400">
                Threshold {isPctMetric ? "(%)" : ""}
              </span>
              <input
                type="number"
                step="0.1"
                value={threshold}
                onChange={(e) => setThreshold(Number(e.target.value))}
                className="mt-1 w-full rounded border border-gray-700 bg-gray-800 px-2 py-1.5 text-sm text-gray-100"
              />
            </label>
          </div>
          <label className="block text-sm">
            <span className="text-gray-400">
              Scope <span className="text-gray-600">(optional)</span>
            </span>
            <input
              value={scope}
              onChange={(e) => setScope(e.target.value)}
              placeholder={
                metric.startsWith("vm.") ? "vm:1234" : "pve-site-a"
              }
              className="mt-1 w-full rounded border border-gray-700 bg-gray-800 px-2 py-1.5 text-sm text-gray-100"
            />
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-300">
            <input
              type="checkbox"
              checked={notifyNtfy}
              onChange={(e) => setNotifyNtfy(e.target.checked)}
            />
            Push to ntfy when this fires
          </label>
          {error && <div className="text-sm text-red-400">{error}</div>}
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded border border-gray-700 bg-gray-800 px-3 py-1.5 text-sm text-gray-200 hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!canSubmit || pending}
              className="rounded bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-500 disabled:opacity-50"
            >
              {pending ? "Creating…" : "Create"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
